import { useEffect, useState } from "react";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import { Typography as MuiTypography, Box } from "@mui/material";
import { styled } from "@mui/system";
import { useParams } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert
} from "@mui/material";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CustomButton from "../../../components/CustomButton/Button";
import AddUserDialog from "./components/AddUserDialog";

import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

const CopyToClipboardComponent = CopyToClipboard as any;

const TypographyValue = styled(MuiTypography)`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

// Note: Keep layout components minimal to respect UI preferences

function GroupedUserAccount() {
  const { userId } = useParams();

  const accessToken: any = localStorage?.getItem("accessToken");
  const [userToAdd, setUserToAdd] = useState("");
  const [isAddLoading, setIsAddLoading] = useState(false);
  // Local state for data management
  const [responseData, setResponseData] = useState<any>(null);
  const [monolithData, setMonolithData] = useState<any>([]);
  const [apiStatus, setApiStatus] = useState<"idle" | "fetching" | "failed">(
    "idle"
  );
  const [monolithApiStatus, setMonolithApiStatus] = useState<
    "idle" | "fetching" | "failed"
  >("idle");

  const [copiedPrimaryUserId, setCopiedPrimaryUserId] = useState("");
  const [copiedUserId, setCopiedUserId] = useState("");
  const [copiedPatronId, setCopiedPatronId] = useState("");
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [addUserError, setAddUserError] = useState("");
  const [openDemergeDialog, setOpenDemergeDialog] = useState(false);
  const [selectedMonolithRow, setSelectedMonolithRow] = useState<any>(null);
  const [isDemergeLoading, setIsDemergeLoading] = useState(false);
  const [topMessage, setTopMessage] = useState<string>("");

  const fetchData = async (accessToken: string, searchString: string) => {
    if (!userId) return;
    fetchGroupedUserAccountData(accessToken, userId);
    fetchMonolithData(accessToken, userId);
  };

  const fetchGroupedUserAccountData = async (
    accessToken: string,
    searchString: string
  ) => {
    // Fetch main grouped user account data
    setApiStatus("fetching");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/users/groupuseraccounts/support/${userId}`,
        {
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      setResponseData(response.data);
      setApiStatus("idle");
    } catch (error) {
      console.error("Error fetching grouped user account data:", error);
      setApiStatus("failed");
    }
  };

  const fetchMonolithData = async (
    accessToken: string,
    searchString: string
  ) => {
    // Fetch monolith data
    setMonolithApiStatus("fetching");
    try {
      const monolithResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/users/groupuseraccounts/support/monolith/${userId}`,
        {
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      setMonolithData(monolithResponse.data || []);
      setMonolithApiStatus("idle");
    } catch (error) {
      console.error("Error fetching monolith data:", error);
      setMonolithApiStatus("failed");
    }
  };

  const handleAddUser = () => {
    handleConfirmAddUser();
  };

  const handleConfirmAddUser = async () => {
    try {
      setIsAddLoading(true);
      setAddUserError("");

      const data = JSON.stringify({
        PrimaryUserId: userId,
        UserId_1: userId,
        UserId_2: userToAdd
      });

      const config: any = {
        method: "post",
        url: `${process.env.REACT_APP_API_BASE_URL}/users/groupuseraccounts/support`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${accessToken}`
        },
        data: data
      };

      const response = await axios(config);
      console.log(JSON.stringify(response.data));

      setOpenAddUserDialog(false);
      setIsAddLoading(false);

      if (userId) {
        fetchData(accessToken, userId);
      }
    } catch (error: any) {
      setIsAddLoading(false);
      console.log(error);

      // Extract error message from API response
      let errorMessage = "Failed to add user, try again!";

      if (
        error?.response?.data?.Failures &&
        error.response.data.Failures.length > 0
      ) {
        // Get the first failure message
        errorMessage = error.response.data.Failures[0].Message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setAddUserError(errorMessage);
    }
  };

  const handleCloseAddUserDialog = () => {
    setOpenAddUserDialog(false);
    setAddUserError("");
  };

  const handleOpenDemergeDialog = (row: any) => {
    setSelectedMonolithRow(row);
    setOpenDemergeDialog(true);
  };

  const handleCloseDemergeDialog = () => {
    setOpenDemergeDialog(false);
    setSelectedMonolithRow(null);
  };

  const handleConfirmDemerge = async () => {
    if (!selectedMonolithRow) return;
    try {
      setIsDemergeLoading(true);
      const payload = {
        FromPatronMasterId: selectedMonolithRow?.FromPatronMasterId,
        ToPatronMasterId: selectedMonolithRow?.ToPatronMasterId
      };

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/users/demergeusers`,
        JSON.stringify(payload),
        {
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      setTopMessage(
        "Demerge request submitted successfully. This process may take sometime to complete. Once completed, the merge record will be removed from the list. If it does not happen then click the refresh button to update the Monolith Group list."
      );
      setOpenDemergeDialog(false);
      setSelectedMonolithRow(null);

      if (userId) {
        fetchMonolithData(accessToken, userId);
      }
    } catch (error) {
      console.error("Error initiating demerge:", error);
    } finally {
      setIsDemergeLoading(false);
    }
  };

  // Clear copied states after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setCopiedPrimaryUserId("");
      setCopiedUserId("");
      setCopiedPatronId("");
    }, 2000);

    return () => clearTimeout(timer);
  }, [copiedPrimaryUserId, copiedUserId, copiedPatronId]);

  const isRefreshing =
    apiStatus === "fetching" || monolithApiStatus === "fetching";

  const groupedUserAccountColumns = [
    {
      id: "UserId",
      label: "UserId",
      minWidth: 300,
      align: "left" as const,
      format: (value: string, row: any) => (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "left"
            }}
          >
            <div
              style={{
                verticalAlign: "middle",
                display: "flex",
                alignItems: "center"
              }}
            >
              {row?.UserId ? row?.UserId?.split("-")[0] : "-"}
            </div>
            {row?.UserId && (
              <div onClick={(e) => e?.stopPropagation()}>
                <CopyToClipboardComponent
                  text={row?.UserId}
                  onCopy={(e: any) => setCopiedUserId(row?.UserId)}
                >
                  <Tooltip
                    title={
                      copiedUserId === row?.UserId
                        ? "Copied to clipboard!"
                        : "Copy UserId to clipboard"
                    }
                    arrow
                    placement="bottom-start"
                  >
                    <IconButton>
                      <ContentCopyIcon
                        style={{
                          marginTop: "0px",
                          paddingTop: "0px",
                          width: "0.9rem ",
                          height: "0.9rem "
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </CopyToClipboardComponent>
              </div>
            )}
          </div>
        </>
      )
    },
    {
      id: "DateAdded",
      label: "DateAdded",
      minWidth: 300,
      align: "left" as const,
      format: (value: string) => (
        <div>{value ? getFormattedDate(value) : "-"}</div>
      )
    },
    {
      id: "FirebaseTokenUpdated",
      label: "FirebaseTokenUpdated",
      minWidth: 300,
      align: "left" as const,
      format: (value: boolean) => (
        <div>{value !== undefined ? (value ? "True" : "False") : "-"}</div>
      )
    }
  ];

  const monolithColumns = [
    {
      id: "FromEmail",
      label: "From Email",
      minWidth: 250,
      align: "left" as const,
      format: (value: string) => <div>{value || "-"}</div>
    },
    {
      id: "FromPatronId",
      label: "From Patron ID",
      minWidth: 150,
      align: "left" as const,
      format: (value: string, row: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>{value || "-"}</span>
          {value && (
            <CopyToClipboardComponent
              text={value}
              onCopy={(e: any) => setCopiedPatronId(value)}
            >
              <Tooltip
                title={
                  copiedPatronId === value
                    ? "Copied to clipboard!"
                    : "Copy Patron ID to clipboard"
                }
                arrow
                placement="bottom-start"
              >
                <IconButton size="small">
                  <ContentCopyIcon
                    style={{ width: "0.8rem", height: "0.8rem" }}
                  />
                </IconButton>
              </Tooltip>
            </CopyToClipboardComponent>
          )}
        </div>
      )
    },
    {
      id: "ToEmail",
      label: "To Email",
      minWidth: 250,
      align: "left" as const,
      format: (value: string) => <div>{value || "-"}</div>
    },
    {
      id: "ToPatronId",
      label: "To Patron ID",
      minWidth: 150,
      align: "left" as const,
      format: (value: string, row: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>{value || "-"}</span>
          {value && (
            <CopyToClipboardComponent
              text={value}
              onCopy={(e: any) => setCopiedPatronId(value)}
            >
              <Tooltip
                title={
                  copiedPatronId === value
                    ? "Copied to clipboard!"
                    : "Copy Patron ID to clipboard"
                }
                arrow
                placement="bottom-start"
              >
                <IconButton size="small">
                  <ContentCopyIcon
                    style={{ width: "0.8rem", height: "0.8rem" }}
                  />
                </IconButton>
              </Tooltip>
            </CopyToClipboardComponent>
          )}
        </div>
      )
    },
    {
      id: "MerchantName",
      label: "Merchant Name",
      minWidth: 200,
      align: "left" as const,
      format: (value: string) => <div>{value || "-"}</div>
    },
    {
      id: "MergeLocation",
      label: "Merge Location",
      minWidth: 150,
      align: "left" as const,
      format: (value: string) => <div>{value || "-"}</div>
    },
    {
      id: "DateCreation",
      label: "Date Creation",
      minWidth: 200,
      align: "left" as const,
      format: (value: string) => (
        <div>{value ? getFormattedDate(value) : "-"}</div>
      )
    },
    {
      id: "Actions",
      label: "Actions",
      minWidth: 140,
      align: "left" as const,
      format: (_: any, row: any) => (
        <div>
          <CustomButton callBack={() => handleOpenDemergeDialog(row)}>
            Demerge
          </CustomButton>
        </div>
      )
    }
  ];

  useEffect(() => {
    if (userId) {
      fetchData(accessToken, userId);
    }
  }, [userId]);

  return (
    <>
      {topMessage && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info">{topMessage}</Alert>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "right",
          justifyContent: "flex-end"
        }}
      >
        <CustomButton callBack={() => setOpenAddUserDialog(true)}>
          Add User
        </CustomButton>
        <IconButton
          onClick={() => fetchGroupedUserAccountData(accessToken, userId || "")}
          disabled={isRefreshing}
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          mt: 3,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <TypographyValue variant="h6">Group Members</TypographyValue>
      </Box>

      <CustomTableWithPagination
        columns={groupedUserAccountColumns}
        data={responseData?.UserGroup || []}
        apiStatus={apiStatus}
        payloadSize={1000}
        currentPage={1}
        totalCount={responseData?.UserGroup?.length || 0}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No group members found"
      />

      {/* Monolith Group Members Section */}
      <Box
        sx={{
          mt: 4,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <TypographyValue variant="h6">Monolith Group Members</TypographyValue>
        <IconButton
          onClick={() => fetchMonolithData(accessToken, userId || "")}
          disabled={isRefreshing}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <CustomTableWithPagination
        columns={monolithColumns}
        data={monolithData || []}
        apiStatus={monolithApiStatus}
        payloadSize={1000}
        currentPage={1}
        totalCount={monolithData?.length || 0}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No monolith data available"
      />

      <Dialog
        open={openDemergeDialog}
        onClose={handleCloseDemergeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
              Confirm Demerge
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDemergeDialog}
              disabled={isDemergeLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to demerge these users?
          </Typography>
          {selectedMonolithRow && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                From Email: {selectedMonolithRow?.FromEmail || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                To Email: {selectedMonolithRow?.ToEmail || "-"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            disabled={isDemergeLoading}
            callBack={handleCloseDemergeDialog}
          >
            Cancel
          </CustomButton>
          <CustomButton
            disabled={isDemergeLoading}
            callBack={handleConfirmDemerge}
          >
            {isDemergeLoading ? "Processing..." : "Demerge"}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <AddUserDialog
        open={openAddUserDialog}
        onClose={handleCloseAddUserDialog}
        onConfirm={handleAddUser}
        loading={isAddLoading}
        error={addUserError}
        userToAdd={userToAdd}
        setUserToAdd={setUserToAdd}
      />
    </>
  );
}

export default GroupedUserAccount;
